/**
 * Right-pane detail view for the selected activity event.
 *
 * Layout: header (id + ts + breadcrumb) → cluster fields →
 * type-specific renderer when present → raw payload as JSON.
 *
 * Special renderers (master plan §UI):
 *   - llm.prompt_assembled  → input prompt + sha256 + token_estimate
 *   - agent.run_end         → final system prompt + final response + duration
 *   - harness.task.start    → params + harness_id
 *   - harness.task.end      → success + cost + tokens
 *   - harness.llm.start/end → prompt/response + usage frame
 *   - harness.subprocess.*  → command + redacted args + exit code + truncated stdout/stderr
 *   - tool.invoke.*         → params/result JSON
 *
 * "View ..." buttons rewrite the parent's filter state to drill in.
 */

import type { ActivityEvent } from "@lexiun/lx-obs";
import { Loader2 } from "lucide-react";

import type { ActivityFilterState } from "./ActivityFilters";

interface Props {
  selectedId: string | null;
  event: ActivityEvent | null;
  context: ActivityEvent[];
  loading: boolean;
  notInView: boolean;
  /** Apply a filter overlay (e.g. drill-down to a session_id). */
  onDrillDown: (next: Partial<ActivityFilterState>) => void;
  /** Jump to another event by id (URL-bound). */
  onSelect: (id: string) => void;
}

export function ActivityDetailPane({
  selectedId,
  event,
  context,
  loading,
  notInView,
  onDrillDown,
  onSelect,
}: Props) {
  if (!selectedId) {
    return (
      <div className="flex min-h-0 items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-sm text-slate-400 dark:border-slate-700">
        Select an event to see its details.
      </div>
    );
  }
  if (loading) {
    return (
      <div className="flex min-h-0 items-center justify-center rounded-md border border-slate-200 p-6 dark:border-slate-700">
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      </div>
    );
  }
  if (notInView || !event) {
    return (
      <div className="flex min-h-0 items-center justify-center rounded-md border border-dashed border-slate-200 p-6 text-sm text-slate-400 dark:border-slate-700">
        Event {selectedId.slice(0, 8)} is not in the current filter window.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col gap-3 overflow-y-auto rounded-md border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm">
          <span className="rounded bg-indigo-100 px-2 py-0.5 font-mono text-xs text-indigo-700 dark:bg-indigo-950 dark:text-indigo-300">
            {event.type}
          </span>
          <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
            {event.source}/{event.subsystem}
          </span>
          <span className="ml-auto text-xs text-slate-400">
            {new Date(event.ts).toLocaleString()}
          </span>
        </div>
        <div className="font-mono text-xs text-slate-400">
          id <span className="text-slate-600 dark:text-slate-300">{event.id}</span>
        </div>
      </header>

      <ClusterFields event={event} onDrillDown={onDrillDown} />
      <SpecialRenderer event={event} onDrillDown={onDrillDown} />

      <details className="rounded-md border border-slate-200 dark:border-slate-700">
        <summary className="cursor-pointer select-none px-3 py-2 text-sm font-medium">
          Raw payload
        </summary>
        <pre className="overflow-x-auto bg-slate-50 p-3 text-xs leading-relaxed dark:bg-slate-950">
{JSON.stringify(event.payload, null, 2)}
        </pre>
      </details>

      {context.length > 0 && (
        <ContextStrip
          events={context.filter((e) => e.id !== event.id)}
          activeId={event.id}
          onSelect={onSelect}
        />
      )}
    </div>
  );
}

function ClusterFields({
  event,
  onDrillDown,
}: {
  event: ActivityEvent;
  onDrillDown: (next: Partial<ActivityFilterState>) => void;
}) {
  const items: Array<{ label: string; value?: string; drill?: Partial<ActivityFilterState> }> = [
    { label: "user_id", value: event.user_id, drill: { user_id: event.user_id } },
    { label: "session_id", value: event.session_id, drill: { session_id: event.session_id } },
    { label: "case_id", value: event.case_id, drill: event.case_id ? { case_id: event.case_id } : undefined },
    { label: "agent_id", value: event.agent_id, drill: event.agent_id ? { agent_id: event.agent_id } : undefined },
    { label: "task_id", value: event.task_id, drill: event.task_id ? { task_id: event.task_id } : undefined },
    { label: "route", value: event.route },
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
      {items
        .filter((i) => i.value)
        .map((i) => (
          <div key={i.label} className="flex items-center gap-1">
            <dt className="text-slate-400">{i.label}</dt>
            <dd className="truncate font-mono text-slate-700 dark:text-slate-300" title={i.value}>
              {i.value}
            </dd>
            {i.drill && (
              <button
                type="button"
                onClick={() => onDrillDown(i.drill!)}
                className="ml-auto rounded border border-slate-200 px-1.5 py-0.5 text-[10px] text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
                title={`Filter to this ${i.label}`}
              >
                drill
              </button>
            )}
          </div>
        ))}
    </dl>
  );
}

function SpecialRenderer({
  event,
  onDrillDown,
}: {
  event: ActivityEvent;
  onDrillDown: (next: Partial<ActivityFilterState>) => void;
}) {
  const p = event.payload as Record<string, unknown>;
  switch (event.type) {
    case "llm.prompt_assembled":
      return (
        <Section title="LLM prompt input">
          <KV k="messages_count" v={asString(p.messages_count)} mono />
          <KV k="token_estimate" v={asString(p.token_estimate)} mono />
          <KV k="sha256" v={asString(p.sha256)} mono />
          {asString(p.prompt) && (
            <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
              {asString(p.prompt)}
            </pre>
          )}
          <DrillButton
            label="View matching agent.run_end"
            onClick={() => onDrillDown({ session_id: event.session_id, type: "agent.run_end" })}
          />
        </Section>
      );

    case "agent.run_end":
      return (
        <Section title="Agent run summary">
          <KV k="success" v={asString(p.success)} />
          <KV k="duration_ms" v={asString(p.duration_ms)} mono />
          <KV k="message_count" v={asString(p.message_count)} mono />
          {asString(p.error) && <KV k="error" v={asString(p.error)} />}
          {asString(p.final_system_prompt) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">
                final_system_prompt
              </summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.final_system_prompt)}
              </pre>
            </details>
          )}
          {asString(p.final_response) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">
                final_response
              </summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.final_response)}
              </pre>
            </details>
          )}
          <DrillButton
            label="View matching prompt input"
            onClick={() =>
              onDrillDown({ session_id: event.session_id, type: "llm.prompt_assembled" })
            }
          />
        </Section>
      );

    case "harness.task.start":
      return (
        <Section title="Harness task start">
          <KV k="harness_id" v={asString(p.harness_id)} mono />
          <KV k="harness_version" v={asString(p.harness_version)} mono />
          <KV k="task_id" v={asString(p.task_id) ?? event.task_id} mono />
          {asString(p.params) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">
                params
              </summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.params)}
              </pre>
            </details>
          )}
          {event.task_id && (
            <div className="flex flex-wrap gap-1">
              <DrillButton
                label="View phase timeline"
                onClick={() => onDrillDown({ task_id: event.task_id })}
              />
              <DrillButton
                label="View all LLM calls in this task"
                onClick={() =>
                  onDrillDown({ task_id: event.task_id, category: "llm" })
                }
              />
            </div>
          )}
        </Section>
      );

    case "harness.task.end":
      return (
        <Section title="Harness task end">
          <KV k="success" v={asString(p.success)} />
          <KV k="status" v={asString(p.status)} />
          <KV k="duration_ms" v={asString(p.duration_ms)} mono />
          <KV k="total_tokens" v={asString(p.total_tokens)} mono />
          <KV k="total_cost_cents" v={asString(p.total_cost_cents)} mono />
          {asString(p.error) && <KV k="error" v={asString(p.error)} />}
          {event.task_id && (
            <DrillButton
              label="View task timeline"
              onClick={() => onDrillDown({ task_id: event.task_id })}
            />
          )}
        </Section>
      );

    case "harness.llm.start":
    case "harness.llm.end":
      return (
        <Section title={event.type}>
          <KV k="provider" v={asString(p.provider)} mono />
          <KV k="model" v={asString(p.model)} mono />
          <KV k="phase" v={asString(p.phase)} mono />
          {asString(p.token_estimate) && (
            <KV k="token_estimate" v={asString(p.token_estimate)} mono />
          )}
          {p.usage != null && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">
                usage
              </summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {JSON.stringify(p.usage, null, 2)}
              </pre>
            </details>
          )}
          {asString(p.prompt) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">
                prompt
              </summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.prompt)}
              </pre>
            </details>
          )}
          {asString(p.response) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">
                response
              </summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.response)}
              </pre>
            </details>
          )}
        </Section>
      );

    case "harness.subprocess.start":
    case "harness.subprocess.end":
      return (
        <Section title={event.type}>
          <KV k="command" v={asString(p.command)} mono />
          <KV k="cwd" v={asString(p.cwd)} mono />
          <KV k="exit_code" v={asString(p.exit_code)} mono />
          <KV k="signal" v={asString(p.signal)} />
          <KV k="duration_ms" v={asString(p.duration_ms)} mono />
          {asString(p.stdout) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">stdout</summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.stdout)}
              </pre>
            </details>
          )}
          {asString(p.stderr) && (
            <details className="rounded-md border border-slate-200 dark:border-slate-700">
              <summary className="cursor-pointer px-2 py-1 text-xs font-medium">stderr</summary>
              <pre className="rounded bg-slate-50 p-2 text-xs dark:bg-slate-950">
                {asString(p.stderr)}
              </pre>
            </details>
          )}
        </Section>
      );

    case "tool.invoke.start":
    case "tool.invoke.end":
    case "harness.tool.invoke.start":
    case "harness.tool.invoke.end":
      return (
        <Section title={event.type}>
          <KV k="tool_name" v={asString(p.tool_name)} mono />
          <KV k="duration_ms" v={asString(p.duration_ms)} mono />
          {asString(p.error) && <KV k="error" v={asString(p.error)} />}
        </Section>
      );

    default:
      return null;
  }
}

function ContextStrip({
  events,
  activeId,
  onSelect,
}: {
  events: ActivityEvent[];
  activeId: string;
  onSelect: (id: string) => void;
}) {
  return (
    <section>
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500">
        Same session, ±60 s
      </h3>
      <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 dark:divide-slate-800 dark:border-slate-700">
        {events.slice(0, 12).map((e) => (
          <li key={e.id}>
            <button
              type="button"
              onClick={() => onSelect(e.id)}
              className={`flex w-full items-center gap-2 px-2 py-1 text-left text-xs transition-colors ${
                activeId === e.id
                  ? "bg-indigo-50 dark:bg-indigo-950"
                  : "hover:bg-slate-50 dark:hover:bg-slate-800"
              }`}
            >
              <span className="font-mono text-slate-400">
                {new Date(e.ts).toLocaleTimeString()}
              </span>
              <span className="font-mono">{e.type}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 rounded-md border border-slate-200 p-3 dark:border-slate-700">
      <h3 className="text-xs font-medium uppercase tracking-wider text-slate-500">{title}</h3>
      <div className="space-y-1">{children}</div>
    </section>
  );
}

function KV({ k, v, mono }: { k: string; v?: string | undefined; mono?: boolean }) {
  if (v == null || v === "") return null;
  return (
    <div className="flex gap-2 text-xs">
      <span className="text-slate-400">{k}</span>
      <span className={mono ? "font-mono text-slate-700 dark:text-slate-300" : "text-slate-700 dark:text-slate-300"}>
        {v}
      </span>
    </div>
  );
}

function DrillButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-md border border-slate-200 px-2 py-1 text-xs hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
    >
      {label}
    </button>
  );
}

function asString(v: unknown): string | undefined {
  if (v == null) return undefined;
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  return undefined;
}
